/*
 * Copyright (C) 2015 - 2018 Kosmos contact@kosmos.fr
 *
 * Projet: actualite-webapp-front
 * Version: 7.4.0-ALPHA-2
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *         http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import {SKELETON_PLACEHOLDERS_PATTERNS, useSkeletonPlaceholder} from '@kuik/modules/skeleton-placeholder.js';

/**
 * Récupère de manière asynchrone les données depuis l'URL spécifiée et affiche le résultat dans l'élément DOM donné.
 * Un placeholder de type skeleton est affiché dans l'élément cible pendant que l'opération de récupération est en cours.
 *
 * @param {HTMLElement} container - L'élément DOM où le contenu récupéré sera affiché.
 * @return {Promise<void>} Une promesse qui se résout lorsque l'opération de récupération est terminée et le contenu est rendu.
 */
async function loadAndDisplayFetchResult(container) {
    const stopLoader = useSkeletonPlaceholder(container, {patterns: SKELETON_PLACEHOLDERS_PATTERNS.GRID, count: 3});
    const response = await fetch(container.dataset.searchUrl);
    if (!response.ok) {
        console.error('Error: ' + response.status);
    }
    const html = await response.text();
    if (container) {
        stopLoader(true);
        container.innerHTML = html;
    }
}

/**
 * Initialise les écouteurs d'événements pour les boutons d'onglets afin de gérer les clics.
 * Au clic, les données sont récupérées dynamiquement et le conteneur associé est mis à jour avec le résultat.
 * Les boutons d'onglets sont identifiés par leur préfixe d'ID 'tab-', et les conteneurs associés sont chargés
 * dynamiquement en fonction de leurs IDs respectifs.
 */
function handleTabClick() {
     document.querySelectorAll('.js-card-last-news button[id^="tab-"]').forEach((button) => {
         button.addEventListener('tab:change', async () => {
             const container = document.getElementById(button.getAttribute('aria-controls'))?.querySelector(':scope > [data-type]')
             await loadAndDisplayFetchResult(container);
         });
     });
}

/**
 * Initialise tous les onglets de nouvelles en récupérant et affichant le contenu depuis une URL fournie.
 *
 * Cette méthode récupère l'élément conteneur pour toutes les nouvelles, extrait l'URL de recherche
 * de ses attributs, et appelle une fonction pour récupérer et afficher le résultat dans le conteneur.
 *
 * @return {Promise<void>} Une promesse qui se résout lorsque les données ont été récupérées et affichées.
 */
async function initAllNewsTabs() {
  const allTypesContainers = document.querySelectorAll('.js-card-last-news [data-type="container-all_news"]');
    for (const allTypesContainer of allTypesContainers) {
      await loadAndDisplayFetchResult(allTypesContainer);
    }
}

document.addEventListener('readystatechange', async function() {
    if (document.readyState === 'complete') {
        await initAllNewsTabs();
        handleTabClick();
    }
});
